import { useMemo, useCallback } from 'react';
import { format, addMinutes } from 'date-fns';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { Visibility, Edit } from '@mui/icons-material';
import FlexCenter from 'components/FlexCenter';

const DELAY_THRESHOLDS = {
    HIGH:   { minutes: 60, color: 'red.main'    },
    MEDIUM: { minutes: 30, color: 'orange.main' },
    LOW:    { minutes: 15, color: 'yellow.main' },
};

const BLINK_ANIMATION = {
    animation: 'blink 1s step-start 0s infinite',
    fontWeight: 'bold',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

export function useFlightColumns({
    timeMode,
    isMobile,
    params,
    handleViewRoute,
    canView,
    canEdit,
    navigate,
    handleViewFlight,
}) {
    const getBlinkStyles = useCallback((delay, dateStr) => {
        if (!delay || isNaN(delay)) return {};

        let today = new Date();
        if (timeMode === 'UTC') {
            today = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
        } else {
            today.setHours(0, 0, 0, 0);
        }

        const flightDate = new Date(dateStr);
        const isTodayOrFuture = flightDate >= today;

        let color;
        if (delay > DELAY_THRESHOLDS.HIGH.minutes)        color = DELAY_THRESHOLDS.HIGH.color;
        else if (delay > DELAY_THRESHOLDS.MEDIUM.minutes) color = DELAY_THRESHOLDS.MEDIUM.color;
        else if (delay > DELAY_THRESHOLDS.LOW.minutes)    color = DELAY_THRESHOLDS.LOW.color;
        else return {};

        return { color, ...(isTodayOrFuture && BLINK_ANIMATION) };
    }, [timeMode]);

    const createBlinkIfBlankColumn = useCallback((
        field,
        headerName,
        compareField,
        _fallbackField,
        delayField,
    ) => ({
        field: timeMode === 'UTC' ? field : `${field}LocalBase`,
        headerName,
        ...(isMobile ? { width: 80 } : { flex: 1.2 }),
        renderCell: ({ row }) => {
            const value        = row[timeMode === 'UTC' ? field        : `${field}LocalBase`];
            const compareValue = row[timeMode === 'UTC' ? compareField : `${compareField}LocalBase`];
            const dateStr      = timeMode === 'UTC' ? row.flightDate : row.flightDateLocalBase;
            const displayValue = value || '';
            let cellStyles = {};

            if (value && delayField) {
                cellStyles = getBlinkStyles(row[delayField] || 0, dateStr);
            } else if (!value && compareValue) {
                const TIMEZONE_OFFSET_MINUTES = params?.find(p => p.param === "TIMEZONE_OFFSET_MINUTES")?.value || '420';
                const now = timeMode === 'UTC' ? new Date() : addMinutes(new Date(), TIMEZONE_OFFSET_MINUTES);
                const diffMinutes = (now - new Date(compareValue)) / 60000;
                cellStyles = getBlinkStyles(diffMinutes, dateStr);
            }

            return (
                <FlexCenter height="100%" width="100%" sx={{ '@keyframes blink': { '60%': { opacity: 0.6 } } }}>
                    <Typography variant="body2" sx={cellStyles}>{displayValue}</Typography>
                </FlexCenter>
            );
        },
    }), [timeMode, params, isMobile, getBlinkStyles]);

    const createTimeColumn = useCallback(({
        field,
        headerName,
        delayField,
        actualField,
        timeMode: tm,
        isMobile: im,
    }) => ({
        field: tm === 'UTC' ? field : `${field}LocalBase`,
        headerName,
        ...(im ? { width: 80 } : { flex: 1.2 }),
        renderCell: ({ row }) => {
            const delay       = row[delayField] || 0;
            const dateStr     = tm === 'UTC' ? row.flightDate : row.flightDateLocalBase;
            const actualValue = actualField ? row[tm === 'UTC' ? actualField : `${actualField}LocalBase`] : null;

            return (
                <FlexCenter
                    height="100%"
                    width="100%"
                    sx={!actualValue && { '@keyframes blink': { '60%': { opacity: 0.6 } } }}
                >
                    <Typography
                        variant="body2"
                        sx={!actualValue && getBlinkStyles(delay, dateStr)}
                    >
                        {row[tm === 'UTC' ? field : `${field}LocalBase`] || ''}
                    </Typography>
                </FlexCenter>
            );
        },
    }), [getBlinkStyles]);

    const columns = useMemo(() => [
        {
            field: "no",
            headerName: "No",
            filterable: false,
            sortable: false,
            ...(isMobile ? { width: 60 } : { flex: 1 }),
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
        },
        {
            field: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase",
            headerName: "FlightDate",
            ...(isMobile ? { width: 100 } : { flex: 1.5 }),
            valueFormatter: (value) => value == null ? '' : format(value, "dd/MM/yy"),
        },
        {
            field: timeMode === 'UTC' ? "flightActualDayDep" : "flightActualDayDepLocalBase",
            headerName: "DepActualDay",
            ...(isMobile ? { width: 100 } : { flex: 1.5 }),
            valueFormatter: (value) => value == null ? '' : format(value, "dd/MM/yy"),
        },
        {
            field: timeMode === 'UTC' ? "flightActualDayArr" : "flightActualDayArrLocalBase",
            headerName: "ArrActualDay",
            ...(isMobile ? { width: 100 } : { flex: 1.5 }),
        },
        {
            field: "flightCarrier",
            headerName: "Carrier",
            ...(isMobile ? { width: 80 } : { flex: 1.2 }),
        },
        {
            field: "flightDesc",
            headerName: "FlightDesc",
            ...(isMobile ? { width: 80 } : { flex: 1.2 }),
        },
        {
            field: "flightNo",
            headerName: "FlightNo",
            ...(isMobile ? { width: 80 } : { flex: 1.2 }),
        },
        {
            field: "dep",
            headerName: "Dep",
            align: "center",
            ...(isMobile ? { width: 60 } : { flex: 1 }),
        },
        {
            field: "arr",
            headerName: "Arr",
            align: "center",
            ...(isMobile ? { width: 60 } : { flex: 1 }),
        },
        {
            field: "sector",
            headerName: "Sector",
            align: "center",
            ...(isMobile ? { width: 100 } : { flex: 1.8 }),
        },
        {
            field: "acType",
            headerName: "ACType",
            ...(isMobile ? { width: 80 } : { flex: 1.2 }),
        },
        {
            field: "flightReg",
            headerName: "Reg",
            ...(isMobile ? { width: 100 } : { flex: 1.5 }),
        },
        {
            field: "flightType",
            headerName: "FlightType",
            ...(isMobile ? { width: 140 } : { flex: 2 }),
        },
        {
            field: timeMode === 'UTC' ? "std" : "stdLocalBase",
            headerName: "Std",
            align: "center",
            ...(isMobile ? { width: 80 } : { flex: 1.2 }),
        },
        {
            field: timeMode === 'UTC' ? "sta" : "staLocalBase",
            headerName: "Sta",
            align: "center",
            ...(isMobile ? { width: 80 } : { flex: 1.2 }),
        },
        createTimeColumn({ field: 'etd', headerName: 'Etd', delayField: 'depDelaysTime', actualField: 'atd', timeMode, isMobile }),
        createTimeColumn({ field: 'eta', headerName: 'Eta', delayField: 'arrDelaysTime', actualField: 'ata', timeMode, isMobile }),
        {
            field: timeMode === 'UTC' ? "doorClose" : "doorCloseLocalBase",
            headerName: "DoorClose",
            align: "center",
            ...(isMobile ? { width: 80 } : { flex: 1.2 }),
        },
        createBlinkIfBlankColumn('atd', 'Atd', 'fullEtd', 'etd', 'depDelaysTime'),
        {
            field: timeMode === 'UTC' ? "takeOff" : "takeOffLocalBase",
            headerName: "TakeOff",
            align: "center",
            ...(isMobile ? { width: 80 } : { flex: 1.2 }),
        },
        {
            field: timeMode === 'UTC' ? "touchDown" : "touchDownLocalBase",
            headerName: "TouchDown",
            align: "center",
            ...(isMobile ? { width: 80 } : { flex: 1.2 }),
        },
        createBlinkIfBlankColumn('ata', 'Ata', 'fullEta', 'eta', 'arrDelaysTime'),
        { field: "tat",           headerName: "TAT",           ...(isMobile ? { width: 80 }  : { flex: 1.2 }) },
        { field: "scheduleTat",   headerName: "ScheduleTAT",   ...(isMobile ? { width: 80 }  : { flex: 1.2 }) },
        { field: "estimatedTat",  headerName: "EstimatedTAT",  ...(isMobile ? { width: 80 }  : { flex: 1.2 }) },
        { field: "tatDiff",       headerName: "TATDiff",       ...(isMobile ? { width: 80 }  : { flex: 1.2 }) },
        { field: "taxiOut",       headerName: "TaxiOut",       ...(isMobile ? { width: 80 }  : { flex: 1.2 }) },
        { field: "taxiIn",        headerName: "TaxiIn",        ...(isMobile ? { width: 80 }  : { flex: 1.2 }) },
        { field: "airMinutes",    headerName: "AirMinutes",    ...(isMobile ? { width: 80 }  : { flex: 1.2 }) },
        { field: "blockMinutes",  headerName: "BlockMinutes",  ...(isMobile ? { width: 80 }  : { flex: 1.2 }) },
        { field: "flightDepDelays", headerName: "DepDelays",   ...(isMobile ? { width: 130 } : { flex: 2   }) },
        { field: "depDelaysTime", headerName: "DepDelaysTime", ...(isMobile ? { width: 80 }  : { flex: 1.2 }) },
        { field: "flightArrDelays", headerName: "ArrDelays",   ...(isMobile ? { width: 130 } : { flex: 2   }) },
        { field: "arrDelaysTime", headerName: "ArrDelaysTime", ...(isMobile ? { width: 80 }  : { flex: 1.2 }) },
        { field: "remarks",       headerName: "Remarks",       ...(isMobile ? { width: 150 } : { flex: 2   }) },
        { field: "sectorType",    headerName: "SectorType",    ...(isMobile ? { width: 120 } : { flex: 2   }) },
        { field: "flightStatus",  headerName: "Status",        ...(isMobile ? { width: 100 } : { flex: 1.5 }) },
        { field: "expectedPaxPerCategory", headerName: "ExpectedPaxPerCategory", ...(isMobile ? { width: 100 } : { flex: 2 }) },
        { field: "paxPerCategory",         headerName: "ActualPaxPerCategory",   ...(isMobile ? { width: 100 } : { flex: 2 }) },
        { field: "expectedNoOfPax", headerName: "ExpectedPax", ...(isMobile ? { width: 60 } : { flex: 1 }) },
        { field: "actualNoOfPax",   headerName: "ActualPax",   ...(isMobile ? { width: 60 } : { flex: 1 }) },
        { field: "noOfPax",         headerName: "Pax",         ...(isMobile ? { width: 60 } : { flex: 1 }) },
        { field: "capacity",        headerName: "Capacity",    ...(isMobile ? { width: 60 } : { flex: 1 }) },
        {
            field: "loadFactor",
            headerName: "LF",
            ...(isMobile ? { width: 60 } : { flex: 1 }),
            valueFormatter: (value) =>
                value !== undefined && value !== null ? `${(value * 100).toFixed(1)}%` : "0%",
        },
        {
            field: "crewRoutes",
            headerName: "CrewRoutes",
            ...(isMobile ? { width: 100 } : { flex: 1.8 }),
            renderCell: ({ row }) => {
                const routes = Array.isArray(row.crewRoutes) ? row.crewRoutes : [];
                return (
                    <Box height="100%" width="100%" display="flex" justifyContent="flex-end" sx={{ textAlign: 'right' }}>
                        <Box sx={{ overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                            {routes.map((route, index) =>
                                route.routeNo && route.routeDate ? (
                                    <Typography
                                        key={index}
                                        variant="body2"
                                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                        onClick={() => handleViewRoute(route.routeDate, route.routeNo)}
                                    >
                                        {`${route.routeNo} ${route.routeDate.substring(0, 5)}`}
                                    </Typography>
                                ) : null
                            )}
                        </Box>
                    </Box>
                );
            },
        },
        { field: "routeCount",          headerName: "RouteCount",    ...(isMobile ? { width: 60 }  : { flex: 1   }) },
        { field: "isGroundTimeRecovery", headerName: "IsGTRecovery",  ...(isMobile ? { width: 100 } : { flex: 1.5 }) },
        { field: "isBlockTimeRecovery",  headerName: "IsBTRecovery",  ...(isMobile ? { width: 100 } : { flex: 1.5 }) },
    ], [timeMode, isMobile, createTimeColumn, createBlinkIfBlankColumn, handleViewRoute]);

    const actionColumn = useMemo(() => {
        const activeCount = [canView, canEdit].filter(Boolean).length;
        if (activeCount === 0) return [];
        const actionWidth = 65 + activeCount * 20;

        return [
            {
                field: 'action',
                headerName: 'Action',
                width: actionWidth,
                filterable: false,
                sortable: false,
                renderCell: (params) => (
                    <FlexCenter height="100%" gap={1}>
                        {canView && (
                            <Tooltip title="View" arrow>
                                <IconButton
                                    aria-label="View"
                                    sx={{ color: "primary.dark" }}
                                    onClick={() => handleViewFlight(params.row)}
                                >
                                    <Visibility />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canEdit && (
                            <Tooltip title="Edit" arrow>
                                <IconButton
                                    aria-label="Edit"
                                    sx={{ color: "primary.dark" }}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        navigate(`/flightschedule/${params.row._id}`);
                                    }}
                                >
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                        )}
                    </FlexCenter>
                ),
            },
        ];
    }, [canView, canEdit, navigate, handleViewFlight]);

    return { columns, actionColumn };
}
